import {
	ClientPortfolioDataCommodityServiceEnum,
	ClientPortfolioPaymentFrequencyEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	type useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { refineContractRequiredMinimum } from "web_myplann/Portfolio/Forms/ContractShared/contractRequiredMinimum";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";
import { optionalDateAsString } from "web_myplann/Shared/Validation/optionalDateAsString";
import { optionalPositiveFloat } from "web_myplann/Shared/Validation/optionalPositiveFloat";
import { trimmedNullable } from "web_myplann/Shared/Validation/trimmedNullable";
import { ValidationLimits } from "web_myplann/Shared/Validation/validationLimits";
import { z } from "zod";

type TranslateFunction = ReturnType<typeof useResource>["t"];

export const getServicesFormSchema = (t: TranslateFunction) => {
	const positiveNumberMessages = {
		positiveMessage: t(
			Resources.Portfolio.AddServices.Validations.positiveNumber,
		),
		maxMessage: t(Resources.Portfolio.AddServices.Validations.maxValue),
	};

	return z
		.object({
			name: trimmedNullable(
				z.string().max(
					ValidationLimits.TextLength.LongName,
					t(Resources.Portfolio.AddServices.Validations.maxLength, {
						max: ValidationLimits.TextLength.LongName,
					}),
				),
			),
			producer: trimmedNullable(
				z.string().max(
					ValidationLimits.TextLength.LongName,
					t(Resources.Portfolio.AddServices.Validations.maxLength, {
						max: ValidationLimits.TextLength.LongName,
					}),
				),
			),
			product: trimmedNullable(
				z.string().max(
					ValidationLimits.TextLength.LongName,
					t(Resources.Portfolio.AddServices.Validations.maxLength, {
						max: ValidationLimits.TextLength.LongName,
					}),
				),
			),
			contractNumber: trimmedNullable(
				z.string().max(
					ValidationLimits.TextLength.Name,
					t(Resources.Portfolio.AddServices.Validations.maxLength, {
						max: ValidationLimits.TextLength.Name,
					}),
				),
			),
			tariff: trimmedNullable(
				z.string().max(
					ValidationLimits.TextLength.Name,
					t(Resources.Portfolio.AddServices.Validations.maxLength, {
						max: ValidationLimits.TextLength.Name,
					}),
				),
			),
			commodityService: z
				.nativeEnum(ClientPortfolioDataCommodityServiceEnum)
				.nullable(),
			unitOfMeasurement: trimmedNullable(
				z.string().max(
					ValidationLimits.TextLength.Name,
					t(Resources.Portfolio.AddServices.Validations.maxLength, {
						max: ValidationLimits.TextLength.Name,
					}),
				),
			),
			pricePerUnit: optionalPositiveFloat(
				ValidationLimits.Amount.Max,
				positiveNumberMessages,
			),
			fixedPriceByFrequency: optionalPositiveFloat(
				ValidationLimits.Amount.Max,
				positiveNumberMessages,
			),
			paymentFrequency: z
				.nativeEnum(ClientPortfolioPaymentFrequencyEnum)
				.nullable(),
			depositAmount: optionalPositiveFloat(
				ValidationLimits.Amount.Max,
				positiveNumberMessages,
			),
			signedAt: optionalDateAsString(DateFormat.Iso),
			startAt: optionalDateAsString(DateFormat.Iso),
			endAt: optionalDateAsString(DateFormat.Iso),
		})
		.superRefine(refineContractRequiredMinimum);
};

export type ServicesFormInput = z.input<
	ReturnType<typeof getServicesFormSchema>
>;
export type ServicesFormOutput = z.output<
	ReturnType<typeof getServicesFormSchema>
>;
